import type { MsgType } from "./MessageComposer";
import type { Recipient } from "./RecipientInput";
import type { RepeatType } from "./SendSettings";

const PRICE: Record<MsgType, number> = { SMS: 20, LMS: 50, MMS: 100 };

const REPEAT_LABELS: Record<RepeatType, string> = {
  none: "",
  daily: "매일 반복",
  weekly: "매주 반복",
  monthly: "매월 반복",
};

function getCountdown(date: string, time: string): string {
  if (!date || !time) return "";
  const target = new Date(`${date}T${time}`);
  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return "";
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  const diffM = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (diffH >= 24) {
    const diffD = Math.floor(diffH / 24);
    return `D-${diffD}`;
  }
  if (diffH > 0) return `${diffH}시간 ${diffM}분 후`;
  return `${diffM}분 후`;
}

interface Props {
  msgType: MsgType;
  message: string;
  subject: string;
  imageFile: File | null;
  recipients: Recipient[];
  scheduleEnabled: boolean;
  scheduleDate: string;
  scheduleTime: string;
  repeatType: RepeatType;
  repeatEndDate: string;
  onSend: () => void;
  sending: boolean;
  perMessageRate?: number | null;
  credit?: number | null;
  batchSize?: number;
  splitThreshold?: number;
  autoSplitSize?: number;
  splitProgress?: { current: number; total: number } | null;
}

const WARN_THRESHOLD = 1000;
const DANGER_THRESHOLD = 5000;

export default function MessagePreview({
  msgType,
  message,
  subject,
  imageFile,
  recipients,
  scheduleEnabled,
  scheduleDate,
  scheduleTime,
  repeatType,
  repeatEndDate,
  onSend,
  sending,
  perMessageRate,
  credit,
  batchSize,
  splitThreshold = 3000,
  autoSplitSize = 2500,
  splitProgress,
}: Props) {
  const price = perMessageRate ?? PRICE[msgType];
  const totalCost = price * recipients.length;
  const safeCredit = credit ?? 0;
  const remainingCount = Math.floor(safeCredit / price);
  const imagePreviewUrl = imageFile ? URL.createObjectURL(imageFile) : null;
  const count = recipients.length;

  // Auto-split logic
  const needsAutoSplit = !scheduleEnabled && count >= splitThreshold;
  const autoSplitChunks = needsAutoSplit ? Math.ceil(count / autoSplitSize) : 0;

  const canSend =
    count > 0 &&
    message.trim().length > 0 &&
    count <= DANGER_THRESHOLD &&
    (!scheduleEnabled || (scheduleDate && scheduleTime));

  // During auto-split, disable the button (progress banner handles display)
  const isSplitSending = sending && splitProgress && splitProgress.total > 0;

  const now = new Date();
  const timeStr = `오전 ${now.getHours() > 12 ? now.getHours() - 12 : now.getHours()}:${String(now.getMinutes()).padStart(2, "0")}`;

  return (
    <div className="space-y-4 md:space-y-5">
      {/* Phone preview */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-4 md:mb-5">
          <div>
            <h3 className="text-white font-semibold text-sm md:text-base">미리보기</h3>
            <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">실제 수신 화면</p>
          </div>
          <span className={`text-[10px] md:text-xs font-semibold px-2 md:px-2.5 py-0.5 md:py-1 rounded-full whitespace-nowrap ${
            msgType === "SMS" ? "bg-violet-500/20 text-violet-400" :
            msgType === "LMS" ? "bg-pink-500/20 text-pink-400" :
            "bg-amber-500/20 text-amber-400"
          }`}>
            {msgType}
          </span>
        </div>

        {/* Phone mockup */}
        <div className="flex justify-center">
          <div className="w-48 md:w-56 bg-[#1a1a2e] rounded-3xl border border-white/[0.12] overflow-hidden">
            {/* Status bar */}
            <div className="bg-[#111122] px-3 md:px-4 py-1.5 md:py-2 flex items-center justify-between">
              <span className="text-white text-[10px] md:text-xs font-medium">9:41</span>
              <div className="flex items-center gap-0.5 md:gap-1">
                <i className="ri-signal-wifi-3-fill text-white text-[10px] md:text-xs"></i>
                <i className="ri-battery-2-charge-fill text-white text-[10px] md:text-xs"></i>
              </div>
            </div>
            {/* Header */}
            <div className="bg-[#111122] px-3 md:px-4 py-2 md:py-3 border-b border-white/[0.06] flex items-center gap-1.5 md:gap-2">
              <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500/40 to-pink-500/40">
                <i className="ri-message-2-line text-white text-[10px] md:text-xs"></i>
              </div>
              <div>
                <p className="text-white text-[10px] md:text-xs font-semibold">010-1234-5678</p>
                <p className="text-gray-500 text-[10px] md:text-xs">문자 메시지</p>
              </div>
            </div>
            {/* Message bubble */}
            <div className="p-3 md:p-4 min-h-[100px] md:min-h-[120px]">
              {message || imageFile ? (
                <div className="bg-[#2a2a3e] rounded-2xl rounded-tl-sm p-2.5 md:p-3 max-w-[90%]">
                  {subject && (
                    <p className="text-white text-[10px] md:text-xs font-bold mb-1 md:mb-1.5 border-b border-white/[0.1] pb-1 md:pb-1.5">{subject}</p>
                  )}
                  {imagePreviewUrl && (
                    <div className="w-full h-16 md:h-24 rounded-lg overflow-hidden mb-1.5 md:mb-2">
                      <img src={imagePreviewUrl} alt="첨부" className="w-full h-full object-cover" />
                    </div>
                  )}
                  {message ? (
                    <p className="text-gray-200 text-[10px] md:text-xs leading-relaxed whitespace-pre-wrap break-words">
                      {message.length > 120 ? message.slice(0, 120) + "..." : message}
                    </p>
                  ) : (
                    <p className="text-gray-500 text-[10px] md:text-xs italic">메시지 없음</p>
                  )}
                  <p className="text-gray-600 text-[10px] md:text-xs mt-1 md:mt-1.5 text-right">{timeStr}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-16 md:h-20 gap-1.5 md:gap-2">
                  <i className="ri-message-2-line text-gray-700 text-xl md:text-2xl"></i>
                  <p className="text-gray-600 text-[10px] md:text-xs">메시지를 입력하세요</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recipient count warning banners */}
      {count >= WARN_THRESHOLD && count < splitThreshold && (
        <div className="flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3.5 py-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-400 text-sm"></i>
          </div>
          <div>
            <p className="text-amber-300 text-xs font-semibold">대량 발송 안내</p>
            <p className="text-amber-400/80 text-[11px] mt-0.5 leading-relaxed">
              {count.toLocaleString()}명 발송은 서버 처리 시간이 걸릴 수 있습니다. 잠시 기다려 주세요.
            </p>
          </div>
        </div>
      )}

      {/* Auto-split banner (3000+ for non-scheduled) */}
      {needsAutoSplit && count < DANGER_THRESHOLD && (
        <div className="flex items-start gap-2.5 bg-emerald-500/10 border border-emerald-500/25 rounded-xl px-3.5 py-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-split-cells-horizontal text-emerald-400 text-sm"></i>
          </div>
          <div>
            <p className="text-emerald-300 text-xs font-semibold">자동 분할 발송</p>
            <p className="text-emerald-400/80 text-[11px] mt-0.5 leading-relaxed">
              {count.toLocaleString()}명은 {autoSplitSize.toLocaleString()}명씩 {autoSplitChunks}회로 나누어 순차 발송됩니다.
              각 분할마다 개별 처리되어 타임아웃 없이 안전하게 발송됩니다.
            </p>
            <p className="text-emerald-500/70 text-[10px] mt-1">
              발송 중 페이지를 닫지 마세요. 총 예상 소요 시간: 약 {autoSplitChunks * 15}~{autoSplitChunks * 25}초
            </p>
          </div>
        </div>
      )}

      {/* Scheduled but large count warning */}
      {scheduleEnabled && count >= splitThreshold && count < DANGER_THRESHOLD && (
        <div className="flex items-start gap-2.5 bg-orange-500/10 border border-orange-500/30 rounded-xl px-3.5 py-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-orange-400 text-sm"></i>
          </div>
          <div>
            <p className="text-orange-300 text-xs font-semibold">예약 발송 — 대량 안내</p>
            <p className="text-orange-400/80 text-[11px] mt-0.5 leading-relaxed">
              {count.toLocaleString()}명 예약 발송은 서버에서 예정된 시간에 처리됩니다.
              즉시 발송이 필요하다면 예약을 해제해 주세요.
            </p>
          </div>
        </div>
      )}

      {count >= DANGER_THRESHOLD && (
        <div className="flex items-start gap-2.5 bg-red-500/10 border border-red-500/30 rounded-xl px-3.5 py-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-400 text-sm"></i>
          </div>
          <div>
            <p className="text-red-300 text-xs font-semibold">발송 불가 — 수신자 초과</p>
            <p className="text-red-400/80 text-[11px] mt-0.5 leading-relaxed">
              1회 최대 발송 가능 수는 {DANGER_THRESHOLD.toLocaleString()}명입니다.
              수신자를 {DANGER_THRESHOLD.toLocaleString()}명 이하로 줄이거나 여러 번 나눠서 발송해 주세요.
            </p>
          </div>
        </div>
      )}

      {/* Send summary */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-6">
        <h3 className="text-white font-semibold text-sm md:text-base mb-3 md:mb-4">발송 요약</h3>
        <div className="space-y-2 md:space-y-3">
          <div className="flex items-center justify-between py-1.5 md:py-2 border-b border-white/[0.06]">
            <span className="text-gray-400 text-xs md:text-sm">메시지 유형</span>
            <span className={`text-xs md:text-sm font-semibold ${
              msgType === "SMS" ? "text-violet-400" :
              msgType === "LMS" ? "text-pink-400" : "text-amber-400"
            }`}>{msgType}</span>
          </div>
          <div className="flex items-center justify-between py-1.5 md:py-2 border-b border-white/[0.06]">
            <span className="text-gray-400 text-xs md:text-sm">수신자 수</span>
            <div className="text-right">
              <span className={`text-xs md:text-sm font-semibold ${count >= DANGER_THRESHOLD ? "text-red-400" : count >= splitThreshold ? "text-emerald-400" : "text-white"}`}>
                {count.toLocaleString()}명
              </span>
              {needsAutoSplit && count < DANGER_THRESHOLD && (
                <p className="text-emerald-400/80 text-[10px] md:text-xs mt-0.5">
                  {autoSplitSize.toLocaleString()}명씩 {autoSplitChunks}회 자동 분할
                </p>
              )}
              {!needsAutoSplit && batchSize && count > batchSize && count < DANGER_THRESHOLD && (
                <p className="text-violet-400 text-[10px] md:text-xs mt-0.5">
                  {batchSize}건씩 {Math.ceil(count / batchSize)}개 배치 분할 발송
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 md:py-2 border-b border-white/[0.06]">
            <span className="text-gray-400 text-xs md:text-sm">건당 비용</span>
            <span className="text-white text-xs md:text-sm font-semibold">₩{price.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between py-1.5 md:py-2 border-b border-white/[0.06]">
            <span className="text-gray-400 text-xs md:text-sm">보유 크레딧</span>
            <div className="text-right">
              <span className="text-white text-xs md:text-sm font-semibold">₩{safeCredit.toLocaleString()}</span>
              <p className="text-violet-400 text-[10px] md:text-xs mt-0.5">약 {remainingCount.toLocaleString()}건 발송 가능</p>
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 md:py-2 border-b border-white/[0.06]">
            <span className="text-gray-400 text-xs md:text-sm">발송 시간</span>
            <div className="text-right">
              {scheduleEnabled && scheduleDate && scheduleTime ? (
                <>
                  <p className="text-white text-xs md:text-sm font-semibold">{scheduleDate} {scheduleTime}</p>
                  <p className="text-violet-400 text-[10px] md:text-xs mt-0.5">{getCountdown(scheduleDate, scheduleTime)}</p>
                </>
              ) : needsAutoSplit ? (
                <span className="text-emerald-400 text-xs md:text-sm font-semibold">순차 분할 발송</span>
              ) : (
                <span className="text-emerald-400 text-xs md:text-sm font-semibold">즉시 발송</span>
              )}
            </div>
          </div>
          {scheduleEnabled && repeatType !== "none" && (
            <div className="flex items-center justify-between py-1.5 md:py-2 border-b border-white/[0.06]">
              <span className="text-gray-400 text-xs md:text-sm">반복 발송</span>
              <div className="text-right">
                <p className="text-pink-400 text-xs md:text-sm font-semibold">{REPEAT_LABELS[repeatType]}</p>
                {repeatEndDate && (
                  <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">{repeatEndDate}까지</p>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between py-2.5 md:py-3 bg-gradient-to-r from-violet-600/10 to-pink-600/10 rounded-xl px-2.5 md:px-3 border border-violet-500/20">
            <span className="text-white text-xs md:text-sm font-semibold">총 예상 비용</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400 text-base md:text-lg font-bold">
              ₩{totalCost.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Validation messages */}
        {!canSend && (
          <div className="mt-3 md:mt-4 space-y-1 md:space-y-1.5">
            {count === 0 && (
              <div className="flex items-center gap-1.5 md:gap-2 text-amber-400/80">
                <div className="w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line text-xs md:text-sm"></i>
                </div>
                <p className="text-[10px] md:text-xs">수신자를 1명 이상 추가하세요</p>
              </div>
            )}
            {!message.trim() && (
              <div className="flex items-center gap-1.5 md:gap-2 text-amber-400/80">
                <div className="w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line text-xs md:text-sm"></i>
                </div>
                <p className="text-[10px] md:text-xs">메시지 내용을 입력하세요</p>
              </div>
            )}
            {count >= DANGER_THRESHOLD && (
              <div className="flex items-center gap-1.5 md:gap-2 text-red-400/80">
                <div className="w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line text-xs md:text-sm"></i>
                </div>
                <p className="text-[10px] md:text-xs">수신자를 {DANGER_THRESHOLD.toLocaleString()}명 이하로 줄여주세요</p>
              </div>
            )}
            {scheduleEnabled && (!scheduleDate || !scheduleTime) && (
              <div className="flex items-center gap-1.5 md:gap-2 text-amber-400/80">
                <div className="w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                  <i className="ri-error-warning-line text-xs md:text-sm"></i>
                </div>
                <p className="text-[10px] md:text-xs">예약 날짜와 시간을 설정하세요</p>
              </div>
            )}
            {scheduleEnabled && scheduleDate && scheduleTime && (() => {
              const target = new Date(`${scheduleDate}T${scheduleTime}`);
              const minTime = new Date(Date.now() + 10 * 60 * 1000);
              return target < minTime ? (
                <div className="flex items-center gap-1.5 md:gap-2 text-red-400/80">
                  <div className="w-3 h-3 md:w-4 md:h-4 flex items-center justify-center">
                    <i className="ri-error-warning-line text-xs md:text-sm"></i>
                  </div>
                  <p className="text-[10px] md:text-xs">현재 시각으로부터 최소 10분 이후로 설정해주세요</p>
                </div>
              ) : null;
            })()}
          </div>
        )}

        {/* Send button */}
        <button
          onClick={() => onSend()}
          disabled={!canSend || sending}
          className={`mt-4 md:mt-5 w-full py-3 md:py-3.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
            canSend && !sending
              ? "bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white hover:scale-[1.02] active:scale-[0.98]"
              : "bg-white/[0.05] text-gray-600 cursor-not-allowed"
          }`}
        >
          {isSplitSending ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              분할 발송 중 {splitProgress!.current}/{splitProgress!.total}
            </>
          ) : sending ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              {batchSize && count > batchSize
                ? `${Math.ceil(count / batchSize)}개 배치 분할 발송 중...`
                : "발송 처리 중..."}
            </>
          ) : scheduleEnabled ? (
            <><i className="ri-calendar-check-line text-base"></i>예약 발송 등록</>
          ) : needsAutoSplit ? (
            <>
              <i className="ri-split-cells-horizontal text-base"></i>
              {count.toLocaleString()}명 {autoSplitChunks}회 자동 분할 발송
            </>
          ) : (
            <><i className="ri-send-plane-fill text-base"></i>
              {count > 0 ? `${count.toLocaleString()}명에게 발송하기` : "발송하기"}
            </>
          )}
        </button>
      </div>
    </div>
  );
}